
import {
  computed, defineComponent, Ref, ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { IFolder, ISharedLable } from '@/types/interfaces';
import {
  FOLDER_SETTINGS_POP_UP_SWITCHER, FOLDER_TYPES, PHOTO_VIEW_TYPE, SHARE_TYPES,
} from '@/types/enums';
import { useDriveStore } from '../../store/driveStore';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import FolderSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/FolderSettingsPopUp.vue';
import DriveFolderSettingsPopUp from '@/modules/drive/components/UI/DriveFolderSettingsPopUp.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import AppRenameFolder from '@/components/AppRenameFolder.vue';
import FolderInfoPopUp from '@/components/BottomPopUp/PopUpTypes/FolderInfoPopUp.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { useMainStore } from '@/store/mainStore';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';

export default defineComponent({
  props: {
    folder: { type: Object as () => IFolder, required: true },
    hideTitle: { type: Boolean, default: false },
    hideAuthor: { type: Boolean, default: false },
    hideDelete: { type: Boolean, default: false },
    showArrow: { type: Boolean, default: false },
    columnView: { type: Boolean, default: false },
    preventOpen: { type: Boolean, default: false },
    disableMove: { type: Boolean, default: false },
    disableShare: { type: Boolean, default: false },
    disableOptions: { type: Boolean, default: false },
  },
  components: {
    AppQuestionPopUp,
    DriveFolderSettingsPopUp,
    FolderSettingsPopUp,
    SharePopUp,
    AppRenameFolder,
    FolderInfoPopUp,
    // eslint-disable-next-line max-len
    // we need async import here to prevent webpack circular imports https://stackoverflow.com/questions/40194697/how-to-have-indirectly-self-nested-components-in-vue-js-2
    MoveToPopUp,
  },
  emits: ['openFolder', 'folderDeleted'],
  setup(props, { emit }) {
    const route = useRoute();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();

    const popUpSwitcher: Ref<FOLDER_SETTINGS_POP_UP_SWITCHER | null> = ref(null);
    const renameMountErrorInfo = ref<undefined | {title: string, error: string}>();

    const isNetwork = computed(() => route.meta.isNetwork);

    const positionData = ref({});

    const systemFolder = computed(
      // eslint-disable-next-line max-len
      () => [mainStore.userSettings.accountSystemSettings.userCasesFolderId, mainStore.userSettings.accountSystemSettings.userUploadsFolderId].includes(props.folder.id),
    );

    /* eslint-disable global-require */
    const folderImage = computed(
      () => (
        props.folder.folderType === FOLDER_TYPES.CASE_FOLDER
          ? require('@/assets/img/icons-img/folder-case.svg')
          : require('@/assets/img/icons-img/folder2.svg')
      ),
    );

    const showDots = computed(
      () => (
        props.columnView && !props.showArrow && !isNetwork.value && !systemFolder.value && !props.disableOptions && !props.preventOpen
      ),
    );

    function deleteFolder() {
      popUpSwitcher.value = null;

      if (props.folder.folderType === FOLDER_TYPES.NON_CASE_FOLDER) {
        driveStore.deleteFolder(props.folder.id).then(() => {
          emit('folderDeleted', props.folder.id);
        });
        // NotificationService.notifySuccess();
      } else {
        driveStore.deleteCase(props.folder.id).then(() => {
          emit('folderDeleted', props.folder.id);
        });
      }
    }

    function saveFolderAsCopy() {
      popUpSwitcher.value = null;
      driveStore.saveFolderAsCopy(props.folder.id);
    }

    function openFolder() {
      if (props.preventOpen) {
        return;
      }
      driveStore.folderFileAmount.push(props.folder.fileAmount);
      emit('openFolder');
    }

    function getPosition(event) {
      const x = event.offsetX;
      const y = event.clientY;
      positionData.value = { x, y };
    }

    function setFolderShared() {

      // is declared but it is not using now. Current code will not work correctly with new app logic

      // const mixItem = driveStore.mixedData.data.find((mix) => mix.folder?.id === props.folder.id);
      // if (mixItem && mixItem.folder) {
      //   mixItem.folder.shared = true;
      // }

      // if (driveStore.currentFolder?.mixedData?.length) {
      //   const mixFolderItem = driveStore.currentFolder.mixedData.find((mix) => mix.folder?.id === props.folder.id);
      //   if (mixFolderItem && mixFolderItem.folder) {
      //     mixFolderItem.folder.shared = true;
      //   }
      // }
    }

    function openRenamePopUp(errorInfo: {title: string, error: string}) {
      renameMountErrorInfo.value = errorInfo;
      popUpSwitcher.value = FOLDER_SETTINGS_POP_UP_SWITCHER.RENAME;
    }

    function setFolderSharedLabels(data: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic

      // const mixItem = driveStore.mixedData.data.find((mix) => mix.folder?.id === props.folder.id);
      // if (mixItem && mixItem.folder) {
      //   mixItem.folder.sharedLabels = data;
      // }

      // if (driveStore.currentFolder?.mixedData?.length) {
      //   const mixFolderItem = driveStore.currentFolder.mixedData.find((mix) => mix.folder?.id === props.folder.id);
      //   if (mixFolderItem && mixFolderItem.folder) {
      //     mixFolderItem.folder.sharedLabels = data;
      //   }
      // }
    }

    const photoUrl = computed(() => {
      if (props.folder?.owner?.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.folder.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });
    const authorName = computed(() => `${props.folder.owner?.firstName || ''} ${props.folder.owner?.lastName || ''}`);

    return {
      mainStore,
      folderImage,
      popUpSwitcher,
      isNetwork,
      photoUrl,
      authorName,
      systemFolder,
      showDots,
      renameMountErrorInfo,
      deleteFolder,
      openFolder,
      openRenamePopUp,
      setFolderShared,
      saveFolderAsCopy,
      setFolderSharedLabels,
      FOLDER_TYPES,
      FOLDER_SETTINGS_POP_UP_SWITCHER,
      SHARE_TYPES,
      getPosition,
      positionData,
    };
  },
});
